
// React import
import React, { useRef, useState, useEffect, useMemo, useCallback } from 'react';
import { useDrag, useDrop } from 'react-dnd'
import { Link } from 'react-router-dom';

// General imports
import _ from 'lodash';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';

// MUI imports
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Paper from '@mui/material/Paper';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import { green, red, amber, grey } from '@mui/material/colors';

// FontAwesome imports
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckSquare, faSquare, faArrowUpRightFromSquare, faCheck, faXmark, faPlane, faShip, faTruck } from '@fortawesome/pro-light-svg-icons';

// Local imports
import { getTradelinesStatus, goalToText } from './utils.js';
import { PIPELINE_TRADELINES as PIPELINE } from '../accounts/AccountFunnelPipeline.js';
import COLORS from '../accounts/AccountFunnelPipeline.colors.js';

dayjs.extend(duration)
dayjs.extend(relativeTime)

function TradelinesPageKanban(props) {

    const { tradelines, opportunities, exchangeRates, onOpen, updateTradeline, accounts, organization } = props;
    const [ maxCards, setMaxCards ] = useState(_.fromPairs(PIPELINE.map(p => ([p, 100]))));
    const [ saving, setSaving ] = useState({});
    const [ sorting, setSorting ] = useState([]);
    const kanbanRef = useRef(null);

    useEffect(() => { kanbanRef.current?.focus(); }, []);

    const onKanbanKeyDown = useCallback((e) => {
        if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
            e.preventDefault();
            kanbanRef.current?.scrollBy({ left: e.key === 'ArrowRight' ? 300 : -300, behavior: 'smooth' });
        }
    }, []);

    useEffect(() => {
        const allIds = tradelines.map(t => t.id);
        if (_.isEmpty(_.xor(allIds, sorting))) return;
        setSorting(allIds);
    }, [ tradelines ]);

    const sortedTradelines = useMemo(() => {
        const tradelinesMap = _.keyBy(tradelines, 'id');
        return _.compact(sorting.map(id => tradelinesMap[id]));
    }, [ sorting, tradelines ]);
 
    async function onUpdateTradeline({ tradeline, pipeline, nextTradelineId }) {

        setSaving({ ...saving, [tradeline.id]: 'working' });

        const newTradeline = _.cloneDeep(tradeline);
        newTradeline.tags = [...(newTradeline.tags || []).filter(t => !t.label.startsWith('Pipeline:')), { label: `Pipeline: ${pipeline}` }];

        setSorting(_sorting => {
            const _s = _sorting.filter(id => id !== tradeline.id);
            const index = !nextTradelineId ? _s.length : _s.findIndex(id => id === nextTradelineId);
            _s.splice(index, 0, tradeline.id);
            return _s;
        });

        try {
            await updateTradeline({ tradeline: newTradeline, setFirst: true });
        } catch (e) {
            console.log(e);
            setSaving({ ...saving, [tradeline.id]: 'error' });
            return;
        }
        setSaving({ ...saving, [tradeline.id]: 'success' });
        await new Promise(res => setTimeout(res, 3000));
        setSaving({ ...saving, [tradeline.id]: null });
    }

    const board = {
        lanes: PIPELINE.map((title, index) => {
            const cards = sortedTradelines.filter(t => {
                const pipeline = (t.tags || []).find(t => t.label.startsWith('Pipeline:'));
                if (!pipeline) return index === 0;
                return pipeline.label === `Pipeline: ${title}`;
            });
            return {
                id: title,
                title: `${title} (${cards.length})`,
                cards: _.slice(cards, 0, maxCards[title]).map((t, index2) => ({
                    id: `${index}-${index2}`,
                    tradeline: t,
                    title: t.title
                })),
                hasMore: cards.length > maxCards[title],
            };
        })
    };

    return (
        <Box ref={kanbanRef} tabIndex={0} onKeyDown={onKanbanKeyDown} sx={{ overflowX: 'auto', outline: 'none' }}>
        <Board
            disableCardDrag={false}
            onCardDragEnd={(board, source, destination, card) => {
                console.log('onCardDragEnd', board, source, destination, card);
            }}
            renderColumnHeader={({ id, index, title }) => {
                const count = title.match(/\((\d+)\)/)?.[1] || '0';
                const name = title.replace(/\s*\(\d+\)/, '');
                return (
                    <Box sx={{ background: COLORS[index], borderRadius: '8px', padding: '8px 14px', minWidth: '250px', maxWidth: '250px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>{name}</Typography>
                        <Typography sx={{ fontSize: '11px', fontWeight: 700, color: 'rgba(255,255,255,0.7)', backgroundColor: 'rgba(255,255,255,0.15)', borderRadius: '10px', px: '8px', py: '1px' }}>{count}</Typography>
                    </Box>
                );
            }}
            renderCard={({ tradeline }) => {
                return (
                    <Card
                        tradeline={tradeline}
                        opportunities={opportunities}
                        onOpen={onOpen}
                        onDrop={onUpdateTradeline}
                        saving={saving[tradeline.id]}
                        exchangeRates={exchangeRates}
                        accounts={accounts}
                        organization={organization}
                    />
                );
            }}
            data={board}
            onDrop={onUpdateTradeline}
            setMaxCards={setMaxCards}
        />
        </Box>
    );
}

export default TradelinesPageKanban;

function Board(props) {
    const { disableCardDrag, onCardDragEnd, renderColumnHeader, renderCard, data, onDrop, setMaxCards } = props;

    return (
        <Box sx={{ display: 'flex', width: `calc(268px * ${data.lanes.length} + 30px)` }}>
            {data.lanes.map((lane, index) => (
                <Column
                    index={index}
                    key={lane.id}
                    data={lane}
                    renderColumnHeader={renderColumnHeader}
                    renderCard={renderCard}
                    onDrop={({ tradeline }) => onDrop({ tradeline, pipeline: lane.id })}
                    onShowMore={() => setMaxCards(maxCards => ({ ...maxCards, [lane.id]: maxCards[lane.id] + 100 }))}
                />
            ))}
        </Box>
    );
}

function Column(props) {
    const { data, renderCard, renderColumnHeader, onDrop, index, onShowMore } = props;

    const [isDropping, setIsDropping] = useState(false);
    const first = useRef();
    const _firstCard = useRef();
    _firstCard.current = data.cards[0];
    const _setIsDropping = useRef(null);
    _setIsDropping.current = setIsDropping;
    const _onDrop = useRef(null);
    _onDrop.current = onDrop;

    const [ , drop ] = useDrop(() => ({ 
        accept: ['TradelineKanbanCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            if (monitor.isOver({ shallow: true })) {
                _setIsDropping.current(monitor.canDrop(item, monitor));
            }
            function isOver() {
                if (monitor.isOver()) {
                    if (monitor.isOver({ shallow: true })) {
                        _setIsDropping.current(monitor.canDrop(item, monitor));
                    } else {
                        _setIsDropping.current(null);
                    }
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    _setIsDropping.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            _onDrop.current({ tradeline: item.tradeline, pipeline: data.id });
        },
        canDrop: (item, monitor) => {
            const { tradeline } = item;
            if (monitor.isOver({ shallow: true }) !== monitor.isOver()) return false;

            if (!_firstCard.current) return true;

            const pipeline = ((tradeline.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;
            const _pipeline = ((_firstCard.current.tradeline.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;

            if (pipeline === _pipeline) return false;

            return true;
        },
    }));

    return (
        <Box ref={drop} sx={{ flex: 1, display: 'flex', flexDirection: 'column', width: '262px', minWidth: '262px', maxWidth: '262px', marginRight: '6px', border: '1px solid #eee', padding: '6px' }}>
            {renderColumnHeader({ id: data.id, title: data.title, index })}
            {data.cards.map(card => (
                <Box key={card.id} sx={{ display: 'flex', flexDirection: 'column', width: '250px', minWidth: '250px', maxWidth: '250px' }}>
                    {renderCard(card)}
                </Box>
            ))}
            {data.hasMore && (
                <Button 
                    sx={{ width: '250px', minWidth: '250px', maxWidth: '250px', mt: 1, textTransform: 'none' }} 
                    variant='text'
                    onClick={onShowMore}
                >
                    Show more...
                </Button>
            )}
            {isDropping && <Box sx={{ flex: 1, background: 'rgba(0,0,0,0.05)' }} />}
        </Box>
    );
}

function Card(props) {
    const { tradeline, opportunities, onOpen, onDrop, saving, exchangeRates, accounts, organization } = props;
    const { title, reminders, tags } = tradeline;
    const [isDragging, setIsDragging] = useState(false);
    const [isDropping, setIsDropping] = useState(false);
    const first = useRef();
    const _tradeline = useRef(null);
    _tradeline.current = tradeline;
    const _setIsDragging = useRef(null);
    _setIsDragging.current = setIsDragging;
    const _setIsDropping = useRef(null);
    _setIsDropping.current = setIsDropping;
    const _onDrop = useRef(null);
    _onDrop.current = onDrop;

    const [, drag] = useDrag(() => ({
        type: 'TradelineKanbanCard',
        item: () => {
            _setIsDragging.current(true);
            return { tradeline: _tradeline.current };
        },
        collect: (monitor) => ({
            isDragging: !!monitor.isDragging()
        }),
        end: () => {
            _setIsDragging.current(false);
        }
    }));

    const [ , drop ] = useDrop(() => ({ 
        accept: ['TradelineKanbanCard'],
        hover: (item, monitor) => {
            if (first.current) return;
            first.current = true;
            _setIsDropping.current(monitor.canDrop(item, monitor));
            function isOver() {
                if (monitor.isOver()) {
                    setTimeout(isOver, 100);
                } else {
                    first.current = false;
                    _setIsDropping.current(null);
                }
            }
            setTimeout(isOver, 100);
        },
        drop: (item, monitor) => {
            const _pipeline = ((_tradeline.current.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;
            _onDrop.current({ tradeline: item.tradeline, pipeline: _pipeline.substr(10), nextTradelineId: _tradeline.current.id});
        },
        canDrop: (item, monitor) => {
            const { tradeline } = item;

            const pipeline = ((tradeline.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;
            const _pipeline = ((_tradeline.current.tags || []).find(t => t.label.startsWith(`Pipeline:`)) || {}).label || `Pipeline: ${PIPELINE[0]}`;

            if (pipeline === _pipeline) return false;

            return true;
        },
    }));

    const status = getTradelinesStatus({ tradeline, opportunities, exchangeRates, organization });

    return (
        <Box ref={drop}>
            {isDropping && (
                <Box sx={{ border: '1px dashed #ccc', paddingTop: '150px', width: '100%', mb: 1, mt: 1, background: 'rgba(0,0,0,0.05)' }} />
            )}
            <Paper ref={drag} sx={{ mb: 1, mt: 1, p: 1.5, width: '250px', minWidth: '250px', maxWidth: '250px', opacity: isDragging ? 0.1 : 1, borderRadius: '8px' }} >
                <Box sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.5 }}>
                    <Typography component={Box} sx={{ fontWeight: 700, flex: 1, fontSize: '13px', lineHeight: 1.3 }}>
                        {title}
                    </Typography>
                    <IconButton size='small' onClick={() => onOpen({ tradeline })} sx={{ color: grey[400], '&:hover': { color: grey[700] }, ml: 0.5 }}>
                        <FontAwesomeIcon icon={faArrowUpRightFromSquare} style={{ fontSize: '11px' }} />
                    </IconButton>
                </Box>
                {tradeline.accountId && (
                    <Typography sx={{ fontSize: '11px', mb: 0.5 }}>
                        <Typography component={Link} to={`/organization/${organization.alias}/accounts/${tradeline.accountId}/details`} sx={{ fontSize: '11px', color: 'primary.main', textDecoration: 'none' }}>
                            {accounts?.find(a => a.id === tradeline.accountId)?.name}
                        </Typography>
                    </Typography>
                )}
                {/* Progress bars per goal */}
                {tradeline.goals?.length > 0 && (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 1, mb: 1 }}>
                        {tradeline.goals.map(g => {
                            const r = status.results?.[g.id];
                            const progress = Math.round((r?.progress || 0) * 100);
                            const barColor = progress >= 100 ? green[500] : progress >= 50 ? amber[500] : red[400];
                            const fraction = new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 });
                            const showCurrency = (g.type === 'revenue' || g.type === 'profit') && g.currency;
                            const fmtVal = (v) => showCurrency ? `${g.currency} ${fraction.format(v)}` : fraction.format(v);
                            const label = goalToText({ goal: g, transportationMethod: tradeline.transportationMethod, html: false }).split(':')[0];
                            return (
                                <Box key={g.id}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.3 }}>
                                        <Typography sx={{ fontSize: '10px', color: '#64748b' }}>{label}</Typography>
                                        <Typography sx={{ fontSize: '10px', fontWeight: 600, color: progress >= 100 ? green[700] : '#334155' }}>
                                            {fmtVal(r?.value || 0)} / {fmtVal(r?.goal || g.value)} ({progress}%)
                                        </Typography>
                                    </Box>
                                    <LinearProgress
                                        variant='determinate'
                                        value={Math.min(100, progress)}
                                        sx={{
                                            height: 4, borderRadius: 2, backgroundColor: grey[200],
                                            '& .MuiLinearProgress-bar': { backgroundColor: barColor, borderRadius: 2 },
                                        }}
                                    />
                                </Box>
                            );
                        })}
                    </Box>
                )}
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
                    <Typography sx={{ fontSize: '10px', color: '#94a3b8' }}>
                        {(tradeline.owner || '').split('@')[0]}
                    </Typography>
                    {tradeline.transportationMethod && (
                        <FontAwesomeIcon
                            icon={tradeline.transportationMethod === 'air' ? faPlane : tradeline.transportationMethod === 'land' ? faTruck : faShip}
                            style={{ fontSize: '10px', color: '#94a3b8' }}
                        />
                    )}
                </Box>
                {tags && tags.filter(t => !t.label.startsWith('Pipeline:')).length > 0 && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', mt: 0.5, gap: '2px' }}>
                        {tags.filter(t => !t.label.startsWith('Pipeline:')).map((t, index) => (
                            <Chip key={index} size='small' label={t.label} sx={{ fontSize: '9px', height: '18px' }} />
                        ))}
                    </Box>
                )}
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
                    {saving === 'working' && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Typography variant='caption' sx={{ marginRight: '6px' }}>
                                Saving...
                            </Typography>
                            <CircularProgress size={18} sx={{ opacity: 0.5 }} />
                        </Box>
                    )}
                    {saving === 'success' && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <FontAwesomeIcon icon={faCheck} color={green[500]} />
                        </Box>
                    )}
                </Box>
                {saving === 'error' && (
                    <Alert severity='error' sx={{ display: 'flex', alignItems: 'center' }}>
                        Saving failed.
                    </Alert>
                )}
            </Paper>
        </Box>
    );
}
